/**
 * Cart Validation Events Module
 * Handles event management and triggers for cart validation
 */

class CartValidationEvents {
  constructor(config = {}) {
    this.config = {
      debug: config.debug || false,
      selectors: {
        addToCartButtons: '[name="add"], .btn-cart, .add-to-cart, .product-form__cart-submit',
        loginForm: 'form[action*="/account/login"]',
        ...config.selectors
      },
      delays: {
        addToCart: 2000,
        postLogin: 2000,
        loginStateCheck: 5000,
        cartUpdateVerification: 500,
        ...config.delays
      },
      ...config
    };

    this.validator = null;
    this.ui = null;
    this.isInitialized = false;
    this.lastCustomerCheck = null;
    this.eventListeners = [];
  }

  /**
   * Initialize event management with validator and UI instances
   */
  init(validator, ui) {
    this.validator = validator;
    this.ui = ui;
    this.isInitialized = true;

    this.setupEventListeners();
    this.log('Event management initialized');
  }

  /**
   * Setup all event listeners
   */
  setupEventListeners() {
    // Add to cart event listeners
    this.addEventListenerWithCleanup('click', document, (e) => {
      if (e.target.matches(this.config.selectors.addToCartButtons)) {
        this.handleAddToCart(e);
      }
    });

    // Cart update event listeners
    this.addEventListenerWithCleanup('cart:updated', document, (e) => {
      this.handleCartUpdated(e);
    });

    // Custom customer login event listeners
    this.addEventListenerWithCleanup('customer:logged_in', document, (e) => {
      this.handleCustomerLogin(e);
    });

    // URL parameter based validation
    this.checkUrlValidationParams();

    // Periodic login state monitoring
    this.startLoginStateMonitoring();

    this.log('All event listeners setup complete');
  }

  /**
   * Handle add to cart events
   */
  handleAddToCart(event) {
    this.log('Add to cart event detected');

    let variantId = null;
    const form = event.target.closest('form[action*="/cart/add"]');
    if (form) {
      const formData = new FormData(form);
      variantId = formData.get('id');
    }

    // Set flag for guest users
    if (!this.validator.isInitialized) {
      this.setGuestCartFlag();
      return;
    }

    if (this.validator.isInitialized) {
      if (variantId && this.validator.markVariantAsRecentlyAdded) {
        this.validator.markVariantAsRecentlyAdded(variantId);
      }

      const addToCartTime = Date.now();
      window.lastAddToCartTime = addToCartTime;

      setTimeout(async () => {
        await this.verifyCartUpdated(addToCartTime);
        this.runValidation();
      }, this.config.delays.addToCart);
    }
  }

  /**
   * Handle cart updated events
   */
  handleCartUpdated(event) {
    this.log('Cart updated event detected');

    if (event.detail?.source === 'cart-validation' || event.detail?.source === 'quantity-enforcer') {
      this.log('Skipping validation - update came from validation system');
      return;
    }

    if (this.validator.isInitialized) {
      setTimeout(async () => {
        await this.verifyCartUpdated(Date.now());
        this.runValidation();
      }, this.config.delays.addToCart);
    }
  }

  /**
   * Handle customer login events
   */
  handleCustomerLogin(event) {
    this.log('Customer login event detected');

    setTimeout(() => {
      this.runValidation();
    }, this.config.delays.postLogin);
  }

  /**
   * Check URL parameters for validation triggers
   */
  checkUrlValidationParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldValidateAndCheckout = urlParams.get('validate_and_checkout') === 'true';
    const originalCheckoutUrl = urlParams.get('original_checkout_url');

    if (shouldValidateAndCheckout && originalCheckoutUrl) {
      this.handleCheckoutValidation(decodeURIComponent(originalCheckoutUrl));
    } else if (this.shouldValidateFromUrl()) {
      setTimeout(() => {
        this.runValidation();
      }, this.config.delays.postLogin);
    }
  }

  /**
   * Handle checkout validation flow
   */
  async handleCheckoutValidation(checkoutUrl) {
    this.log('Checkout validation flow triggered');

    if (!this.validator.isInitialized) {
      this.log('Validator not initialized for checkout flow');
      window.location.href = checkoutUrl;
      return;
    }

    // Show validation UI
    const overlay = this.ui.showValidationProgress();

    try {
      // Run validation
      const { itemsToRemove } = await this.validator.validateCart();
      
      if (itemsToRemove.length > 0) {
        const result = await this.validator.removeItems(itemsToRemove);
        
        if (result.success) {
          // Remove the progress overlay
          overlay.remove();
          
          // Show detailed summary of removed items with checkout options
          this.ui.showCheckoutValidationSummary(result.removedItems, checkoutUrl);
          this.cleanupUrl();
          return;
        }
      }

      // No items removed - proceed directly to checkout
      this.ui.showValidationComplete(overlay, checkoutUrl, 1500);
      this.cleanupUrl();

    } catch (error) {
      this.log('Error during checkout validation:', error);
      // On error, just redirect to checkout
      setTimeout(() => {
        window.location.href = checkoutUrl;
      }, 2000);
    }
  }

  async verifyCartUpdated(addToCartTime) {
    try {
      const cart = await this.validator.fetchCart();
      
      if (!cart.items || cart.items.length === 0) {
        this.log('Cart appears empty after add - waiting for update');
        await new Promise(resolve => setTimeout(resolve, this.config.delays.cartUpdateVerification));
        
        const retryCart = await this.validator.fetchCart();
        if (!retryCart.items || retryCart.items.length === 0) {
          this.log('Cart still empty - skipping validation');
          return false;
        }
      }

      const timeSinceAdd = Date.now() - addToCartTime;
      if (timeSinceAdd < 2000) {
        this.log('Item added very recently - waiting for cart to stabilize');
        await new Promise(resolve => setTimeout(resolve, this.config.delays.cartUpdateVerification));
      }

      return true;
    } catch (error) {
      this.log('Error verifying cart update:', error);
      return true;
    }
  }

  /**
   * Run standard cart validation
   */
  async runValidation(options = {}) {
    if (!this.validator.isInitialized) {
      this.log('Validation skipped - validator not initialized');
      return;
    }

    try {
      // Set flag to prevent cart drawer from auto-opening
      this.setCartDrawerPreventFlag(true);

      const { itemsToRemove, cart } = await this.validator.validateCart();

      if (itemsToRemove.length > 0) {
        const legitimateRemovals = itemsToRemove.filter(item => {
          return item.reason === 'already purchased' || 
                 item.reason === 'part of purchased pack' || 
                 item.reason === 'duplicate item in cart';
        });

        if (legitimateRemovals.length === 0) {
          this.log('No legitimate items to remove - skipping removal');
          this.setCartDrawerPreventFlag(false);
          return;
        }

        if (legitimateRemovals.length === cart.items.length && cart.items.length === 1) {
          const onlyItem = legitimateRemovals[0];
          if (onlyItem.reason !== 'already purchased' && onlyItem.reason !== 'part of purchased pack') {
            this.log('Preventing removal of last cart item unless it was purchased');
            this.setCartDrawerPreventFlag(false);
            return;
          }
        }

        const result = await this.validator.removeItems(legitimateRemovals);
        
        if (result.success) {
          // Show notification unless specifically disabled
          if (!options.skipNotification) {
            this.ui.showRemovalNotification(result.removedItems);
          }

          // Update cart UI
          this.ui.updateCartIcon(result.cart);

          // Trigger cart update events
          this.dispatchCartUpdateEvent(result.cart);

          // Clear guest flag since validation completed
          this.clearGuestCartFlag();
        }
      }

      // Clear the prevent flag after a short delay
      setTimeout(() => {
        this.setCartDrawerPreventFlag(false);
      }, 1000);

    } catch (error) {
      this.log('Error during cart validation:', error);
      // Clear the prevent flag on error too
      this.setCartDrawerPreventFlag(false);
    }
  }

  /**
   * Set/clear flag to prevent cart drawer from auto-opening
   */
  setCartDrawerPreventFlag(prevent) {
    if (prevent) {
      window.cartValidationInProgress = true;
      document.body.setAttribute('data-cart-validation-in-progress', 'true');
    } else {
      window.cartValidationInProgress = false;
      document.body.removeAttribute('data-cart-validation-in-progress');
    }
    
    this.log('Cart drawer prevent flag set to:', prevent);
  }

  /**
   * Start monitoring for login state changes
   */
  startLoginStateMonitoring() {
    if (!window.Shopify?.routes?.root) {
      this.log('Shopify routes not available for login monitoring');
      return;
    }

    setInterval(() => {
      fetch(`${window.Shopify.routes.root}account`)
        .then(response => {
          const isLoggedIn = !response.url.includes('/account/login');
          
          if (this.lastCustomerCheck === false && isLoggedIn) {
            this.log('Login state change detected via monitoring');
            setTimeout(() => {
              this.runValidation();
            }, 500);
          }
          
          this.lastCustomerCheck = isLoggedIn;
        })
        .catch(() => {
          // Ignore errors - this is just background monitoring
        });
    }, this.config.delays.loginStateCheck);
  }

  /**
   * Check if validation should run based on URL patterns
   */
  shouldValidateFromUrl() {
    const currentUrl = window.location.href;
    const urlParams = new URLSearchParams(window.location.search);

    const suspiciousPatterns = [
      urlParams.get('customer_posted') === 'true',
      currentUrl.includes('/account'),
      currentUrl.includes('checkout'),
      document.referrer.includes('/account/login'),
      urlParams.has('return_to'),
      !sessionStorage.getItem('cart_validated_this_session')
    ];

    if (suspiciousPatterns.some(pattern => pattern)) {
      sessionStorage.setItem('cart_validated_this_session', 'true');
      return true;
    }

    return false;
  }

  /**
   * Set flag for guest users who add items to cart
   */
  setGuestCartFlag() {
    try {
      localStorage.setItem('needsCartValidationAfterLogin', 'true');
      this.log('Set guest cart validation flag');
    } catch (e) {
      this.log('Could not set localStorage flag');
    }
  }

  /**
   * Clear guest cart flag
   */
  clearGuestCartFlag() {
    try {
      localStorage.removeItem('needsCartValidationAfterLogin');
      this.log('Cleared guest cart validation flag');
    } catch (e) {
      this.log('Could not clear localStorage flag');
    }
  }

  /**
   * Clean up URL parameters
   */
  cleanupUrl() {
    const cleanUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
  }

  /**
   * Dispatch cart update event
   */
  dispatchCartUpdateEvent(cart) {
    const event = new CustomEvent('cart:updated', {
      detail: { 
        cart,
        source: 'cart-validation' // Flag to indicate this update is from validation
      }
    });
    document.dispatchEvent(event);
  }

  /**
   * Add event listener with cleanup tracking
   */
  addEventListenerWithCleanup(eventType, element, handler, options = {}) {
    element.addEventListener(eventType, handler, options);
    
    this.eventListeners.push({
      eventType,
      element,
      handler,
      options
    });
  }

  /**
   * Clean up all event listeners
   */
  cleanup() {
    this.eventListeners.forEach(({ eventType, element, handler, options }) => {
      element.removeEventListener(eventType, handler, options);
    });
    
    this.eventListeners = [];
    this.log('All event listeners cleaned up');
  }

  /**
   * Debug logging
   */
  log(message, data = null) {
    if (this.config.debug) {
      console.log(`[CartValidationEvents] ${message}`, data || '');
    }
  }
}

// Export for use in other modules
window.CartValidationEvents = CartValidationEvents; 